/**
 * Converts any article title into a clean, SEO-friendly URL slug.
 * Example: "52.87 km Solo & Unsupported Crossing" -> "52-87-km-solo-unsupported-crossing"
 */
export function slugify(text: string): string {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')        // Replace spaces with -
        .replace(/&/g, '-and-')      // Replace & with 'and'
        .replace(/[^\w\-]+/g, '')    // Remove all non-word chars
        .replace(/\-\-+/g, '-')      // Replace multiple - with single -
        .replace(/^-+/, '')          // Trim - from start of text
        .replace(/-+$/, '');         // Trim - from end of text
}

/**
 * Dynamically updates document title, description, and JSON-LD schema
 * when viewing an individual blog post.
 */
export function updateArticleSEO(article: {
    title: string;
    excerpt?: string;
    author?: string;
    date?: string;
    slug?: string;
    image?: string;
}) {
    const currentSlug = article.slug || slugify(article.title);
    const articleUrl = `https://openwatersyndicate.com/the-log/${currentSlug}`;
    const description = article.excerpt || `${article.title} - Open Water Syndicate Expedition Log`;
    const authorName = article.author || 'Taras V.';

    // 1. Update Title & Meta Description
    document.title = `${article.title} | OWS Expedition Journal`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
        metaDesc.setAttribute('content', description);
    }

    // 2. Update Open Graph / Twitter Tags
    const setMeta = (property: string, content: string) => {
        let el = document.querySelector(`meta[property="${property}"]`);
        if (!el) {
            el = document.createElement('meta');
            el.setAttribute('property', property);
            document.head.appendChild(el);
        }
        el.setAttribute('content', content);
    };

    setMeta('og:title', article.title);
    setMeta('og:description', description);
    setMeta('og:url', articleUrl);
    if (article.image) setMeta('og:image', article.image);

    // 3. Inject / Update Dynamic JSON-LD Schema for AI Models
    const schemaId = 'dynamic-article-schema';
    let scriptEl = document.getElementById(schemaId) as HTMLScriptElement;
    if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = schemaId;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
    }

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        'headline': article.title,
        'description': description,
        'url': articleUrl,
        'datePublished': article.date || new Date().toISOString().split('T')[0],
        'author': {
            '@type': 'Person',
            'name': authorName,
            'jobTitle': 'Lead Expedition Guide & 4x Lake Ontario Crossing Veteran'
        },
        'publisher': {
            '@type': 'Organization',
            'name': 'Open Water Syndicate',
            'logo': 'https://openwatersyndicate.com/assets/logo.png'
        }
    };

    scriptEl.textContent = JSON.stringify(schemaData);
}